import type { ComponentPropsWithoutRef, ElementType } from "react";

/**
 * Props for LiveAnnounce component.
 */
export type LiveAnnounceProperties<C extends ElementType = "div"> = Omit<
  ComponentPropsWithoutRef<C>,
  "as"
> & {
  /**
   * The element type to render as.
   * @default "div"
   */
  as?: C;
  /**
   * The politeness level for screen reader announcements.
   * - "polite": Announces when the user is idle. Use for alerts, notifications, etc
   * - "assertive": Interrupts the user immediately. Avoid using this most of the time. Only use when every change should be announced
   * @default "polite"
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-live
   */
  politeness?: "assertive" | "polite";
};
/**
 * LiveAnnounce component renders content that is announced
 * to screen readers when the content changes. Useful for dynamic status
 * updates, form validation messages, and other real-time notifications.
 */
export const LiveAnnounce = <C extends ElementType = "div">({
  as,
  children,
  politeness = "polite",
  ...properties
}: LiveAnnounceProperties<C>) => {
  const Component = as ?? "div";
  return (
    <Component {...properties} aria-atomic="true" aria-live={politeness}>
      {children}
    </Component>
  );
};
LiveAnnounce.displayName = "LiveAnnounce";
